import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Users as UsersIcon, X, Eye, EyeOff } from 'lucide-react';
import {
  Field,
  TextInput,
  SelectInput,
  FormSection,
  PrimaryButton,
  GhostButton,
} from '../ui/Field';
import { PasswordStrength } from '../ui/PasswordStrength';
import type { User, Company } from '../../types/api';

// ─────────────────────────────────────────────────────────────
// UserForm — Operations Deck redesign
//
//   01 · Identity         (first name, last name)
//   02 · Access           (email, role, company, username)
//   03 · Password         (highlighted) — set or update credentials
// ─────────────────────────────────────────────────────────────

const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[$@$!%*?&]).{8,}$/;
const PASSWORD_HINT =
  'Min 8 chars with uppercase, lowercase, number and special ($@$!%*?&).';

const emptyUser: Partial<User> = {
  username: '',
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  companyId: null,
};

export interface UserErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  username?: string;
  password?: string;
  confirmPassword?: string;
  roleId?: string;
  companyId?: string;
}

interface Role {
  id: number;
  name: string;
}

interface UserFormProps {
  open: boolean;
  editing: User | null;
  saving: boolean;
  isSuperAdmin: boolean;
  authCompanyId: number | null;
  companies: Company[];
  roles: Role[];
  onCancel: () => void;
  onSave: (payload: {
    form: Partial<User>;
    roleId: number;
    confirmPassword: string;
  }) => Promise<void> | void;
}

function validate(
  form: Partial<User>,
  roleId: number,
  confirmPassword: string,
  isEdit: boolean,
): UserErrors {
  const e: UserErrors = {};
  if (!form.firstName?.trim()) e.firstName = 'First name is required';
  if (!form.lastName?.trim()) e.lastName = 'Last name is required';
  if (!form.email?.trim()) e.email = 'Email is required';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
    e.email = 'Enter a valid email address';
  if (!form.username?.trim()) e.username = 'Username is required';
  if (!isEdit) {
    if (!form.password?.trim()) e.password = 'Password is required';
    else if (!PASSWORD_PATTERN.test(form.password)) e.password = PASSWORD_HINT;
  }
  if (isEdit && form.password && !PASSWORD_PATTERN.test(form.password)) {
    e.password = PASSWORD_HINT;
  }
  if (form.password && form.password !== confirmPassword)
    e.confirmPassword = 'Passwords must match';
  if (!roleId) e.roleId = 'Role is required';
  if (!form.companyId && form.companyId !== 0)
    e.companyId = 'Company is required';
  return e;
}

export function UserForm({
  open,
  editing,
  saving,
  isSuperAdmin,
  authCompanyId,
  companies,
  roles,
  onCancel,
  onSave,
}: UserFormProps) {
  const [form, setForm] = useState<Partial<User>>(emptyUser);
  const [roleId, setRoleId] = useState<number>(3);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<UserErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Hydrate when opening / target changes
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({ ...editing, password: '' });
      setRoleId(editing.userRoles?.[0]?.roleId ?? 3);
    } else {
      setForm({
        ...emptyUser,
        companyId: isSuperAdmin ? null : authCompanyId,
      });
      setRoleId(roles.length > 0 ? roles[roles.length - 1].id : 3);
    }
    setConfirmPassword('');
    setErrors({});
    setSubmitted(false);
    setShowPassword(false);
  }, [open, editing, isSuperAdmin, authCompanyId, roles]);

  // Body scroll lock
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Escape + autofocus first field
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onCancel();
    };
    document.addEventListener('keydown', onKey);
    const t = setTimeout(() => {
      const el = document.querySelector<HTMLInputElement>(
        '[data-field="firstName"] input',
      );
      el?.focus();
    }, 60);
    return () => {
      document.removeEventListener('keydown', onKey);
      clearTimeout(t);
    };
  }, [open, saving, onCancel]);

  if (!open) return null;

  const isEdit = !!editing;
  const set = <K extends keyof User>(key: K, value: User[K]) =>
    setForm((p) => ({ ...p, [key]: value }));

  // Email change → mirror to username on create
  const handleEmailChange = (email: string) => {
    setForm((p) => ({
      ...p,
      email,
      username: editing ? p.username : email,
    }));
    liveValidate({ ...form, email, username: editing ? form.username : email });
  };

  const liveValidate = (next: Partial<User>) => {
    if (submitted) setErrors(validate(next, roleId, confirmPassword, isEdit));
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    const e = validate(form, roleId, confirmPassword, isEdit);
    setErrors(e);
    if (Object.keys(e).length > 0) {
      const first = Object.keys(e)[0];
      const el = document.querySelector<HTMLElement>(`[data-field="${first}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    await onSave({ form, roleId, confirmPassword });
  };

  const errorCount = submitted ? Object.keys(errors).length : 0;
  const passwordMismatch = !!form.password && form.password !== confirmPassword;

  const subtitle = isEdit ? `Users · Editing #${editing?.id ?? ''}` : 'Users · New entry';
  const title = isEdit ? 'Edit user' : 'Add user';

  // Company display for non-superadmins (read-only chip).
  const companyForReadonly = companies.find((c) => c.id === authCompanyId);
  const companyReadonlyLabel = companyForReadonly?.companyName
    ?? (authCompanyId != null ? `Company #${authCompanyId}` : '—');

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="user-form-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: 'rgba(15, 23, 32, 0.45)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '5vh 16px 5vh',
        overflowY: 'auto',
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onCancel();
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 760,
          background: 'var(--color-panel)',
          borderRadius: 14,
          boxShadow: '0 32px 80px rgba(0,0,0,0.30)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
        }}
      >
        {/* ── Header ─────────────────────────────────────────── */}
        <header
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            padding: '20px 24px 18px',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', minWidth: 0 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 9,
                background: 'rgba(11,74,111,0.10)',
                color: 'var(--color-accent)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                marginTop: 2,
              }}
            >
              <UsersIcon size={20} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 10.5,
                  textTransform: 'uppercase',
                  letterSpacing: 1.4,
                  color: 'var(--color-text-dim)',
                  fontFamily: 'var(--font-mono)',
                  fontWeight: 500,
                }}
              >
                {subtitle}
              </div>
              <h2
                id="user-form-title"
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 24,
                  fontWeight: 500,
                  letterSpacing: -0.4,
                  color: 'var(--color-text)',
                  marginTop: 3,
                  marginBottom: 4,
                  lineHeight: 1.2,
                }}
              >
                {title}
              </h2>
              <p
                style={{
                  fontSize: 13,
                  color: 'var(--color-text-mid)',
                  margin: 0,
                  lineHeight: 1.45,
                  maxWidth: 520,
                }}
              >
                Fields marked with{' '}
                <span style={{ color: 'var(--color-bad)' }}>*</span> are required.
              </p>
            </div>
          </div>

          <button
            onClick={onCancel}
            aria-label="Close"
            disabled={saving}
            style={{
              width: 30,
              height: 30,
              borderRadius: 6,
              background: 'transparent',
              border: 'none',
              color: 'var(--color-text-dim)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-panel-alt)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <X size={18} />
          </button>
        </header>

        {/* Error summary */}
        {submitted && errorCount > 0 && (
          <div
            role="alert"
            style={{
              padding: '10px 24px',
              background: 'rgba(185,28,28,0.06)',
              borderBottom: '1px solid rgba(185,28,28,0.15)',
              color: 'var(--color-bad)',
              fontSize: 12.5,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <strong style={{ fontWeight: 600 }}>
              {errorCount} field{errorCount === 1 ? '' : 's'} need
              {errorCount === 1 ? 's' : ''} attention
            </strong>
            <span style={{ color: 'var(--color-text-mid)' }}>
              — please review the highlighted inputs.
            </span>
          </div>
        )}

        {/* Body */}
        <div
          style={{
            padding: '22px 24px 24px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 26,
            background: 'var(--color-panel)',
          }}
        >
          {/* 01 Identity */}
          <FormSection number={1} title="Identity">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="First name" required error={submitted ? errors.firstName : undefined}>
                <div data-field="firstName">
                  <TextInput
                    value={form.firstName ?? ''}
                    placeholder="Dave"
                    autoComplete="off"
                    error={submitted && !!errors.firstName}
                    onChange={(e) => {
                      set('firstName', e.target.value);
                      liveValidate({ ...form, firstName: e.target.value });
                    }}
                  />
                </div>
              </Field>
              <Field label="Last name" required error={submitted ? errors.lastName : undefined}>
                <div data-field="lastName">
                  <TextInput
                    value={form.lastName ?? ''}
                    placeholder="Cooper"
                    autoComplete="off"
                    error={submitted && !!errors.lastName}
                    onChange={(e) => {
                      set('lastName', e.target.value);
                      liveValidate({ ...form, lastName: e.target.value });
                    }}
                  />
                </div>
              </Field>
            </div>
          </FormSection>

          {/* 02 Access */}
          <FormSection number={2} title="Access">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Email"
                required
                error={submitted ? errors.email : undefined}
                style={{ gridColumn: '1 / -1' } as never}
              >
                <div data-field="email">
                  <TextInput
                    type="email"
                    value={form.email ?? ''}
                    placeholder="dave.cooper@example.com"
                    autoComplete="off"
                    error={submitted && !!errors.email}
                    onChange={(e) => handleEmailChange(e.target.value)}
                  />
                </div>
              </Field>

              <Field label="Role" required error={submitted ? errors.roleId : undefined}>
                <div data-field="roleId">
                  <SelectInput
                    value={roleId}
                    error={submitted && !!errors.roleId}
                    onChange={(e) => {
                      const next = Number(e.target.value);
                      setRoleId(next);
                      if (submitted) {
                        setErrors(validate(form, next, confirmPassword, isEdit));
                      }
                    }}
                  >
                    {roles.length > 0 ? (
                      roles.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))
                    ) : (
                      <>
                        <option value={1}>SuperAdmin</option>
                        <option value={2}>CompanyAdmin</option>
                        <option value={3}>CompanyUser</option>
                      </>
                    )}
                  </SelectInput>
                </div>
              </Field>

              <Field label="Company" required error={submitted ? errors.companyId : undefined}>
                <div data-field="companyId">
                  {isSuperAdmin ? (
                    <SelectInput
                      value={form.companyId ?? ''}
                      error={submitted && !!errors.companyId}
                      onChange={(e) => {
                        const v = e.target.value !== '' ? Number(e.target.value) : null;
                        set('companyId', v);
                        liveValidate({ ...form, companyId: v });
                      }}
                    >
                      <option value="">— Select company —</option>
                      {roles.find((r) => r.id === roleId)?.name === 'SuperAdmin' && (
                        <option value={0}>ALL companies</option>
                      )}
                      {companies.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.companyName}
                        </option>
                      ))}
                    </SelectInput>
                  ) : (
                    <TextInput
                      value={companyReadonlyLabel}
                      readOnly
                      style={{
                        background: 'var(--color-panel-alt)',
                        color: 'var(--color-text-mid)',
                        cursor: 'not-allowed',
                      }}
                    />
                  )}
                </div>
              </Field>

              <Field
                label="Username"
                hint="Auto-set to the email address."
                style={{ gridColumn: '1 / -1' } as never}
              >
                <div data-field="username">
                  <TextInput
                    value={form.username ?? ''}
                    readOnly
                    style={{
                      background: 'var(--color-panel-alt)',
                      color: 'var(--color-text-mid)',
                      cursor: 'not-allowed',
                      fontFamily: 'var(--font-mono)',
                      fontSize: 13,
                    }}
                  />
                </div>
              </Field>
            </div>
          </FormSection>

          {/* 03 Password */}
          <FormSection number={3} title="Password" highlight>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Password"
                required={!isEdit}
                error={submitted ? errors.password : undefined}
              >
                <div data-field="password" style={{ position: 'relative' }}>
                  <TextInput
                    type={showPassword ? 'text' : 'password'}
                    value={form.password ?? ''}
                    placeholder={isEdit ? 'Leave blank to keep current' : ''}
                    autoComplete="new-password"
                    error={submitted && !!errors.password}
                    style={{ paddingRight: 40 }}
                    onChange={(e) => {
                      set('password', e.target.value);
                      liveValidate({ ...form, password: e.target.value });
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    style={{
                      position: 'absolute',
                      right: 8,
                      top: 19,
                      width: 28,
                      height: 28,
                      borderRadius: 6,
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--color-text-dim)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                <PasswordStrength value={form.password ?? ''} alwaysShow={!isEdit} />
              </Field>
              <Field
                label="Confirm password"
                required={!isEdit && !!form.password}
                error={
                  passwordMismatch
                    ? 'Passwords must match'
                    : submitted
                      ? errors.confirmPassword
                      : undefined
                }
              >
                <div data-field="confirmPassword">
                  <TextInput
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    placeholder={isEdit ? 'Leave blank to keep current' : ''}
                    autoComplete="new-password"
                    error={passwordMismatch || (submitted && !!errors.confirmPassword)}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (submitted) {
                        setErrors(validate(form, roleId, e.target.value, isEdit));
                      }
                    }}
                  />
                </div>
              </Field>
            </div>
          </FormSection>
        </div>

        {/* Footer */}
        <footer
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '14px 24px',
            background: 'var(--color-panel-alt)',
            borderTop: '1px solid var(--color-border)',
          }}
        >
          <div
            style={{
              fontSize: 11.5,
              color: 'var(--color-text-dim)',
              fontFamily: 'var(--font-mono)',
              letterSpacing: 0.6,
            }}
          >
            {isEdit
              ? `LAST UPDATE · ${formatTs(editing?.lastModifiedDate ?? editing?.createdDate)}`
              : 'NEW USER'}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <GhostButton onClick={onCancel} disabled={saving}>
              Cancel
            </GhostButton>
            <PrimaryButton onClick={handleSubmit} disabled={saving || passwordMismatch}>
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add user'}
            </PrimaryButton>
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

// ── helpers ──────────────────────────────────────────────────
function formatTs(d?: string | null): string {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return d;
  }
}
